// rosepine — Dawn (light) / Rosé Pine (dark), accent = rose.
// Mapped from the upstream scheme and AA-validated (≥4.5:1 on every
// contract-mandated text pair, both modes) before landing — see
// scratchpad/rosepine-notes.md for the per-pair tables.

import type { ThemeDefinition } from "./types.js";

export const rosepine: ThemeDefinition = {
  palette: {
    light: {
      brand500: "#d7827e", brand600: "#8c5452", brand100: "#fae8e1",
      // DS-11/DS-21 — this is the family the split exists for. The fill stays
      // exactly where it was, but its ink flips: `inkOnBrand` #312e44 on this
      // dark rose is 2.18:1, so pressing a brand button here USED TO ERASE ITS
      // OWN LABEL. Dawn's own near-white takes it to 5.77 and keeps the family
      // warmth, the same move IB-11 already made for danger/warn below.
      brandText: "#8c5452", brandPressed: "#8c5452", inkOnBrandPressed: "#fffaf3",
      bg: "#faf4ed", surface: "#fffaf3", surface2: "#f2e9e1",
      // borderStrong: DS-9 (was #cecacd -> 1.56 vs surface) + DS-23 retune vs
      // surface2, the surface that actually decides this token (see design-tokens.ts)
      border: "#dfdad9", borderStrong: "#858184", scrim: "rgba(0,0,0,0.5)",
      // DS-8: ink2 #6a6688 and ink3 #6a6586 were the SAME colour give or take
      // three points, and inverted by a hair (4.53 vs 4.59) — Dawn's own subtle
      // (3.67) and muted (2.48) are both under the floor, so both had been
      // darkened until they collided. ink2 backs off to a real second step:
      // three levels that read apart and all clear AA.
      //
      // DS-22 (Antenore, 2026-07-26) — comfort pass, route C2, the same move
      // as Latte above and for the same reason: primary was 6.07:1, under AAA,
      // because Dawn's `surface2` is its darkest surface. 6.07 -> 7.03, ladder
      // steps 0.89/0.59 -> 1.42/0.80. Surfaces untouched (see the C1 note in
      // catppuccin-light for why lightening them was the wrong lever).
      ink: "#4e496c", ink2: "#5c577f", ink3: "#676282", inkOnBrand: "#312e44",
      // IB-11: brand500 #d7827e is a LIGHT rose, so inkOnBrand is dark. But
      // danger/warn are DARK fills and need the opposite ink. Dawn's own
      // near-white (its `surface`) keeps the family warmth: 5.51 on danger,
      // 5.24 on warn, against 2.29/2.41 when they reused inkOnBrand.
      inkOnDanger: "#fffaf3", inkOnWarn: "#fffaf3",
      pr: "#8f6020", prFill: "#fbead8",
      complete: "#3a656d", completeFill: "#eaeae5", // DS-13: was #406f77 (4.66 on surface2, borderline) → 5.37, comfortable floor for reduced vision
      danger: "#955265", dangerFill: "#f6e4e0",
      warn: "#8f6020", warnFill: "#fbead8",
    },
    dark: {
      brand500: "#ebbcba", brand600: "#a28286", brand100: "#2f2834",
      // DS-11/DS-21: the pressed fill is brand600 untouched, but brandText is a
      // touch lighter than it — brand600 itself reads 4.12 on `brand100` and
      // 4.39 on `surface2`, i.e. under the floor on BOTH, which is precisely
      // the reading job it was failing. #a88a8d clears it (4.54 / 4.83).
      brandText: "#a88a8d", brandPressed: "#a28286", inkOnBrandPressed: "#191724",
      bg: "#191724", surface: "#1f1d2e", surface2: "#26233a",
      // borderStrong: DS-9 (was #524f67 -> 2.10 vs surface) + DS-23 retune vs surface2
      border: "#403d52", borderStrong: "#74718b", scrim: "rgba(0,0,0,0.5)",
      // DS-8: same collision as Dawn — ink2 #908caa (Rosé Pine's own subtle) and
      // ink3 #8e8aa5 sat 0.14 apart, so the two lower tiers were one tier
      // wearing two names. Official `muted` #6e6a86 is 2.94, far under the
      // floor, which is why ink3 had already been lightened up into subtle's
      // lap. ink2 moves up instead: 11.51 > 6.09 > 4.57, with room to see it.
      // DS-8 comfort: primary already reads 11.51 here, so the headroom was
      // free — ink2/ink3 were just parked near the floor. 11.51 > 8.59 > 6.31.
      ink: "#e0def4", ink2: "#c4c0d9", ink3: "#a8a4c0", inkOnBrand: "#191724",
      inkOnDanger: "#191724", inkOnWarn: "#191724",
      pr: "#f6c177", prFill: "#30282c",
      complete: "#9ccfd8", completeFill: "#252937",
      danger: "#eb6f92", dangerFill: "#311f30",
      warn: "#f6c177", warnFill: "#30282c",
    },
  },
  intensityScale: {
    light: {
      work: { fill: "#286983", on: "#ffffff", soft: "#e5e6e2", text: "#575279" },
      moderate: { fill: "#ea9d34", on: "#000000", soft: "#fbead8", text: "#575279" },
      heavy: { fill: "#b4637a", on: "#000000", soft: "#f6e4e0", text: "#575279" },
      max: { fill: "#907aa9", on: "#000000", soft: "#f1e8e6", text: "#575279" },
      none: { fill: "#f2e9e1", on: "#000000", soft: "#f4ede8", text: "#6a6688" },
    },
    dark: {
      work: { fill: "#31748f", on: "#ffffff", soft: "#1a2030", text: "#e0def4" },
      moderate: { fill: "#f6c177", on: "#000000", soft: "#30282c", text: "#e0def4" },
      heavy: { fill: "#eb6f92", on: "#000000", soft: "#311f30", text: "#e0def4" },
      max: { fill: "#c4a7e7", on: "#000000", soft: "#2b2539", text: "#e0def4" },
      none: { fill: "#26233a", on: "#ffffff", soft: "#21202e", text: "#908caa" },
    },
  },
  // Rosé Pine has no true green, so the low end starts cool (foam/pine) and
  // warms through gold→rose→love — same low→high meaning, stops kept distinct.
  heatmapRamp: {
    light: ["#56949f", "#90a36a", "#ea9d34", "#d97e6a", "#b4637a"],
    dark: ["#9ccfd8", "#bcc78a", "#ffc061", "#ea9d8f", "#e67093"],
  },
  // Monochrome effort ramp on the rose accent (see design-tokens.ts
  // effortRamp for the semantics; pairs AA-validated by effortRampContrast).
  effortRamp: {
    light: [
      { fill: "#f1dcda", on: "#33110f" },
      { fill: "#e0b1ae", on: "#33110f" },
      { fill: "#cd7f7a", on: "#33110f" },
      { fill: "#b14b43", on: "#ffffff" },
      { fill: "#7a332e", on: "#ffffff" },
    ],
    dark: [
      { fill: "#5f201c", on: "#f3e9e8" },
      { fill: "#862d27", on: "#f3e9e8" },
      { fill: "#b63d35", on: "#f3e9e8" },
      { fill: "#d16861", on: "#260e0d" },
      { fill: "#e19d98", on: "#260e0d" },
    ],
  },
};
